import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useResetUserSubscriptionPackage,
  useUpdateSubscriptionPackage,
} from "@/hooks/use-mutations";
import {
  useGetSubscriptionPackages,
  useGetUser,
  useGetUserBins,
} from "@/hooks/use-queries";
import { cardStyleFor } from "@/lib/card-style";

type Bin = {
  id: number;
  bin: string;
  provider: string;
  network?: string;
};

const hasDefaultPrice = (provider: string) =>
  provider === "wsb" || provider === "ptp";

const PackageRow = ({
  pkg,
  bin,
  whiteLabelId,
  userId,
}: {
  pkg: SubscriptionPackage;
  bin: Bin;
  whiteLabelId: string;
  userId: string;
}) => {
  const queryClient = useQueryClient();
  const isCustom = bin.provider !== "int";
  const showDefault = hasDefaultPrice(bin.provider);
  const [price, setPrice] = useState(String(parseFloat(pkg.price)));
  const [defaultPrice, setDefaultPrice] = useState(
    String(parseFloat(pkg.defaultPrice) || ""),
  );

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: ["subscriptionPackages", whiteLabelId],
    });

  const { mutate: updatePackage, isPending: isSaving } =
    useUpdateSubscriptionPackage({ whiteLabelId, onSuccess: refresh });
  const { mutate: resetPackage, isPending: isResetting } =
    useResetUserSubscriptionPackage({ whiteLabelId, onSuccess: refresh });
  const isPending = isSaving || isResetting;

  const handleSave = () =>
    updatePackage({
      packageId: pkg.id,
      data: {
        userId,
        price: Number(price),
        ...(showDefault && { defaultPrice: Number(defaultPrice) }),
      },
    });

  const handleReset = () => resetPackage({ packageId: pkg.id, userId });

  return (
    <div
      className="rounded-md border border-[#DAE1EA] p-2.5 text-xs"
      style={{ boxShadow: "0px 1px 1px 0px #0000000D" }}
    >
      <span className="font-medium">{pkg.name}</span>
      <div className="mt-2 grid grid-cols-2 gap-2.5">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-normal">
            {isCustom ? "Custom Price ($)" : "Price ($)"}
          </label>
          <Input
            type="number"
            step="0.01"
            className="rounded"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        {showDefault && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-normal">Default Price ($)</label>
            <Input
              type="number"
              step="0.01"
              className="rounded"
              value={defaultPrice}
              onChange={(e) => setDefaultPrice(e.target.value)}
            />
          </div>
        )}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          isLoading={isResetting}
          disabled={isPending}
          className="w-20 rounded-md text-xs"
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          type="button"
          isLoading={isSaving}
          disabled={isPending || !price || (showDefault && !defaultPrice)}
          className="bg-primary-500 hover:bg-primary-500/90 w-20 rounded-md text-xs"
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const SubSetting = ({ userData }: { userData: any }) => {
  const { data: admin } = useGetUser();
  const whiteLabelId = userData?.whiteLabelId || admin?.whiteLabelId || "";
  const { data: bins = [], isLoading: isBinsLoading } = useGetUserBins(
    userData?.id,
  );
  const { data: packages = [], isLoading: isPackagesLoading } =
    useGetSubscriptionPackages(whiteLabelId, userData?.id);

  return (
    <div className="mt-4 px-4">
      {isBinsLoading || isPackagesLoading ? (
        <p className="text-xs text-[#A5ACB6]">Loading subscription...</p>
      ) : bins.length === 0 ? (
        <p className="text-xs text-[#A5ACB6]">No bins found.</p>
      ) : (
        <Tabs defaultValue={String(bins[0].id)}>
          <TabsList className="no-scrollbar *:data-[state=active]:text-primary-500 flex w-full justify-start overflow-x-auto rounded border border-[#3D69C580] bg-[#F9F9F9] p-0 *:shrink-0 *:rounded *:text-[10px] *:text-[#07173D66] *:data-[state=active]:bg-[#DFEEFF]">
            {bins.map((bin: Bin) => (
              <TabsTrigger key={bin.id} value={String(bin.id)}>
                {`${cardStyleFor(bin.provider, bin.network).tierLabel} (${bin.bin})`}
              </TabsTrigger>
            ))}
          </TabsList>
          {bins.map((bin: Bin) => {
            const binPackages = (packages as SubscriptionPackage[]).filter(
              (pkg) => pkg.binId === bin.id,
            );
            return (
              <TabsContent key={bin.id} value={String(bin.id)}>
                <div className="mt-4 space-y-2">
                  {binPackages.length === 0 ? (
                    <p className="text-xs text-[#A5ACB6]">No packages found.</p>
                  ) : (
                    binPackages.map((pkg) => (
                      <PackageRow
                        key={`${pkg.id}-${pkg.price}-${pkg.defaultPrice}`}
                        pkg={pkg}
                        bin={bin}
                        whiteLabelId={whiteLabelId}
                        userId={userData.id}
                      />
                    ))
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
};

export default SubSetting;
